import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { CeoTable } from './ceo-table'
import { ProjectedValue } from './projected-value'

const percent = (value) => formatPercent(value, 1, 1)
const money = (value) => formatUSD(value, 0, 0)
const short = (date) => date.slice(5)

/** MON-81 每条线的颜色：主角 --chart-1，4 条对照 --chart-2～5，5 条均线取同一套主题色的 --chart-7～10、12 */
const SERIES_COLORS = {
  subject: 'hsl(var(--chart-1))',
  yesterday: 'hsl(var(--chart-2))',
  lastWeek: 'hsl(var(--chart-3))',
  lastMonth: 'hsl(var(--chart-4))',
  lastYear: 'hsl(var(--chart-5))',
  lastWeekAvg: 'hsl(var(--chart-7))',
  lastYearWeekAvg: 'hsl(var(--chart-8))',
  monthToDate: 'hsl(var(--chart-9))',
  lastMonthAvg: 'hsl(var(--chart-10))',
  lastYearMonthAvg: 'hsl(var(--chart-12))',
}

/** 安装日 / 区间：单日队列给安装日，均线给区间；早于可信历史时为空 */
const spanText = (series) => {
  if (series.kind !== 'avg') return series.installDate ?? '-'
  return series.range ? `${short(series.range.start)}～${short(series.range.end)}` : '-'
}

/** 较主角（点）= 主角 D180 − 该行 D180：正数 = 主角更好标绿，负数标红，±1 点内不标色 */
const pointsText = (delta) => {
  const points = Math.round(delta * 1000) / 10 || 0
  return `${points > 0 ? '+' : ''}${points.toFixed(1)}`
}
const deltaTone = (delta) => {
  if (delta == null || Math.abs(delta) <= 0.01) return null
  return delta > 0 ? 'text-success' : 'text-destructive'
}

/** 检查点列：D0 | D3 |（neku 才有 D8）| D7 | D14 | D30 | D90 | D180；D0 永远是实测 */
const COLUMNS = [0, 3, 8, 7, 14, 30, 90, 180]

/** MON-86 外推标记（CEO 选定 B 版）：每格数值后固定宽度标记槽，外推格槽内小号上标「预」，实测格槽为空，整列数字右边缘对齐 */
export const PROJECTED_NOTE = '上标 预 = 按成熟倍数外推'

/** delta 只在 D180 格给出：hover 写较主角点差，数值按点差着色（主角行不传） */
function PointCell({ point, delta }) {
  if (!point)
    return (
      <TableCell className="text-right text-muted-foreground">
        <ProjectedValue>-</ProjectedValue>
      </TableCell>
    )
  const lines = [point.projected ? '按成熟倍数外推' : '实测']
  if (delta != null) lines.push(`较主角 ${pointsText(delta)} 点`)
  return (
    <TableCell
      title={lines.join('\n')}
      className={cn('whitespace-nowrap text-right tabular-nums', deltaTone(delta))}
    >
      <ProjectedValue projected={point.projected}>{percent(point.roas)}</ProjectedValue>
    </TableCell>
  )
}

function PaybackCell({ series }) {
  const { payback } = series.checkpoints
  if (!payback) {
    const known = series.curve[series.curve.length - 1]?.roas != null
    return (
      <TableCell className="text-right tabular-nums text-muted-foreground">
        <ProjectedValue>{known ? '>180' : '-'}</ProjectedValue>
      </TableCell>
    )
  }
  return (
    <TableCell className="whitespace-nowrap text-right tabular-nums">
      <ProjectedValue projected={payback.projected}>D{payback.age}</ProjectedValue>
    </TableCell>
  )
}

const rowTitle = (series) => {
  const lines = [
    series.kind === 'avg'
      ? series.range
        ? `${series.range.start} ～ ${series.range.end}（${series.days} 天），花费 ${money(series.cost)}`
        : '区间早于可信历史'
      : series.installDate
        ? `安装日 ${series.installDate}，花费 ${money(series.cost)}`
        : '安装日早于可信历史',
  ]
  if (series.measuredAge != null) lines.push(`实测到 D${series.measuredAge}，之后按成熟倍数外推`)
  if (series.modeledCostShare != null && series.modeledCostShare < 0.999) {
    lines.push(`D180 覆盖 ${formatPercent(series.modeledCostShare, 0, 0)} 花费（其余天龄低于外推下限或无倍数样本）`)
  }
  if (series.noise === 'low-cost') lines.push('日花费 < $100，只作参考')
  return lines.join('\n')
}

function SeriesRow({ series, columns }) {
  const head = series.kind === 'subject'
  const daily = series.days > 0 ? series.installs / series.days : null
  return (
    <TableRow
      title={rowTitle(series)}
      className={cn(head && 'bg-muted/50 font-medium', series.noise && 'text-muted-foreground')}
    >
      <TableCell className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          <span className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: SERIES_COLORS[series.key] }} />
          {series.label}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">{spanText(series)}</TableCell>
      <TableCell className="text-right tabular-nums">{series.days ? money(series.dailyCost) : '-'}</TableCell>
      <TableCell className="text-right tabular-nums">{daily == null ? '-' : formatNumber(daily)}</TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">{formatUSD(series.cpi, 2, 2)}</TableCell>
      {columns.map((n) => (
        <PointCell key={n} point={series.checkpoints[`d${n}`]} delta={n === 180 ? series.deltaD180 : null} />
      ))}
      <PaybackCell series={series} />
    </TableRow>
  )
}

/**
 * MON-81 检查点表：主角置顶高亮 + 4 条对照 + 5 条均线；外推值与实测同色，只靠外推标记区分（MON-86）。
 * MON-84 去掉「较主角 D180」列，点差（主角 − 该行）放到 D180 格的 hover 并给数值着色
 */
export function CompareTable({ data }) {
  const columns = COLUMNS.filter((n) => data.horizons.includes(n))
  return (
    <CeoTable>
      <TableHeader className="bg-muted">
        <TableRow>
          <TableHead className="whitespace-nowrap">队列</TableHead>
          <TableHead className="whitespace-nowrap">安装日 / 区间</TableHead>
          <TableHead className="whitespace-nowrap text-right">日花费</TableHead>
          <TableHead className="whitespace-nowrap text-right" title="均线为日均安装数">
            安装数
          </TableHead>
          <TableHead className="text-right">CPI</TableHead>
          {columns.map((n) => (
            <TableHead
              key={n}
              className="whitespace-nowrap text-right"
              title={n === 180 ? '悬停看较主角 = 主角 D180 − 该行 D180（都可能是外推值）' : undefined}
            >
              D{n}
            </TableHead>
          ))}
          <TableHead className="whitespace-nowrap text-right">回本日</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.series.map((series) => (
          <SeriesRow key={series.key} series={series} columns={columns} />
        ))}
      </TableBody>
    </CeoTable>
  )
}
